import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  V2Alert,
  V2Badge,
  V2Button,
  V2Card,
  V2Modal,
  LoadingState,
  Icon,
} from '@sakhtar/design-system/react';
import { tenantBasePath } from '../../lib/tenant.js';
import { apiGet } from '../../lib/apiClient.js';
import { useCapabilities } from '../../lib/capabilities.js';
import { YarInactiveNotice, useModuleYar } from './moduleYar.js';
import {
  type OperationalAlert,
  getToolEndpoint,
  parseOperationalAlerts,
} from './operationalAlerts.ts';
import './yar.css';

/**
 * REQ-AI-ACTION-2026:
 * Operational Helper card on module hub pages. Renders alerts and action triggers
 * with preview/confirm flow and strict domain permission checks.
 * Not a standalone chat; direct conversational exploration leads to central /ai.
 */
export function HubYarCard({ moduleId }: { readonly moduleId: string }) {
  const state = useModuleYar(moduleId);
  const { yar, wiring, active, slug } = state;
  const navigate = useNavigate();
  const capabilities = useCapabilities();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<readonly OperationalAlert[]>([]);
  const [selectedAlert, setSelectedAlert] = useState<OperationalAlert | null>(null);

  useEffect(() => {
    if (!active || !wiring || !slug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const endpoint = getToolEndpoint(moduleId, slug);
    if (!endpoint) {
      setLoading(false);
      return;
    }

    apiGet<unknown>(endpoint)
      .then((data) => {
        if (cancelled) return;
        const parsed = parseOperationalAlerts(moduleId, data);
        setAlerts(parsed);
      })
      .catch(() => {
        if (cancelled) return;
        setError('دریافت اطلاعات و هشدارهای عملیاتی ماژول با خطا مواجه شد.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [moduleId, active, wiring, slug]);

  if (!yar || !wiring) return null;
  const centralAiPath = `${tenantBasePath(slug)}/ai`;

  return (
    <section
      className="yar-hub"
      aria-label={yar.name}
      style={{ ['--yar-main' as string]: yar.main, ['--yar-light' as string]: yar.light }}
    >
      <V2Card className="yar-hub__card">
        <div className="yar-hub__head">
          <img className="yar-hub__avatar" src={yar.image} alt="" />
          <div className="yar-hub__intro">
            <div className="yar-hub__title">
              <strong>{yar.name}</strong>
              <V2Badge tone={active ? 'success' : 'warn'}>{active ? 'فعال' : 'غیرفعال'}</V2Badge>
            </div>
            <p className="yar-hub__sub">
              یار عملیاتی {yar.moduleLabel}؛ پایش بلادرنگ اقلام باز، مغایرت‌ها و هشدارهای عملیاتی
              ماژول.
            </p>
          </div>
          <div className="yar-hub__actions">
            <Link className="yar-hub__link" to={centralAiPath}>
              مشاوره و تحلیل در ساختار یار ›
            </Link>
          </div>
        </div>

        {!active ? (
          <div className="yar-hub__notice">
            <YarInactiveNotice state={state} />
          </div>
        ) : loading ? (
          <div className="yar-hub__loading">
            <LoadingState label="در حال بررسی شاخص‌ها و هشدارهای عملیاتی..." />
          </div>
        ) : error ? (
          <div className="yar-hub__notice">
            <V2Alert tone="warn">
              {error}
              <V2Button
                size="sm"
                variant="ghost"
                onClick={() => state.reload()}
                style={{ marginInlineStart: 8 }}
              >
                تلاش مجدد
              </V2Button>
            </V2Alert>
          </div>
        ) : alerts.length === 0 ? (
          <div className="yar-hub__clean">
            <div className="yar-clean-state">
              <span className="yar-clean-state__badge">✓</span>
              <span>
                تمامی وضعیت‌ها و اسناد عملیاتی این ماژول در حالت مطلوب قرار دارند و هشدار معوقی
                شناسایی نشد.
              </span>
            </div>
          </div>
        ) : (
          <div className="yar-hub__alerts" role="region" aria-label="هشدارهای عملیاتی یار">
            <div className="yar-alerts-grid">
              {alerts.slice(0, 4).map((alert) => (
                <div
                  key={alert.id}
                  className={`yar-alert-card yar-alert-card--${alert.severity}`}
                >
                  <div className="yar-alert-card__top">
                    <V2Badge tone={alert.severity === 'critical' ? 'danger' : 'warn'}>
                      {alert.severity === 'critical' ? 'بحرانی' : 'اقدام لازم'}
                    </V2Badge>
                    <strong className="yar-alert-card__title">{alert.title}</strong>
                  </div>
                  <p className="yar-alert-card__desc">{alert.description}</p>
                  <div className="yar-alert-card__bottom">
                    <span className="yar-alert-card__metric">
                      {alert.metricLabel}: <strong className="sk-num">{alert.metricValue}</strong>
                    </span>
                    <V2Button
                      size="sm"
                      variant="primary"
                      onClick={() => setSelectedAlert(alert)}
                    >
                      {alert.actionLabel}
                    </V2Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </V2Card>

      {selectedAlert ? (
        <V2Modal
          open={true}
          title={`پیش‌نمایش اقدام عملیاتی: ${selectedAlert.actionLabel}`}
          onClose={() => setSelectedAlert(null)}
          footer={
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
              <V2Button variant="ghost" onClick={() => setSelectedAlert(null)}>
                انصراف
              </V2Button>
              <V2Button
                variant="primary"
                disabled={!capabilities.hasCapability(selectedAlert.requiredCapability)}
                onClick={() => {
                  const target = `${tenantBasePath(slug)}${selectedAlert.targetRoute}`;
                  setSelectedAlert(null);
                  navigate(target);
                }}
              >
                تأیید و هدایت به اقدام
              </V2Button>
            </div>
          }
        >
          <div className="yar-action-preview">
            <div className="yar-action-preview__row">
              <span className="yar-action-preview__label">موجودیت هدف:</span>
              <strong>{selectedAlert.title}</strong>
            </div>
            <div className="yar-action-preview__row">
              <span className="yar-action-preview__label">شناسه رکورد:</span>
              <code className="sk-num">{selectedAlert.recordId}</code>
            </div>
            <div className="yar-action-preview__row">
              <span className="yar-action-preview__label">{selectedAlert.metricLabel}:</span>
              <strong className="sk-num">{selectedAlert.metricValue}</strong>
            </div>
            <div className="yar-action-preview__row">
              <span className="yar-action-preview__label">عملیات پیشنهادی:</span>
              <span>{selectedAlert.proposedAction}</span>
            </div>

            <div style={{ marginTop: 16 }}>
              <V2Alert
                tone={
                  capabilities.hasCapability(selectedAlert.requiredCapability) ? 'info' : 'warn'
                }
              >
                {capabilities.hasCapability(selectedAlert.requiredCapability)
                  ? 'این اقدام از طریق اعتبارسنجی دامنه هدایت می‌شود. کلیه قوانین قطعی اسناد، کنترل دوره‌های مالی و سقف اختیارات سازمانی پابرجا هستند.'
                  : `شما مجوز سیستمی لازم (${selectedAlert.requiredCapability}) برای تغییر یا ثبت نهایی این رکورد را ندارید.`}
              </V2Alert>
            </div>
          </div>
        </V2Modal>
      ) : null}
    </section>
  );
}
